<template>
  <el-col :span="props.spanColumn">
    <div
      v-if="!props.settings.hideLabel"
      class="label"
    >
      {{ props.label }}
    </div>
    <el-autocomplete
      ref="autocompleteRef"
      v-model="displayValue"
      :value-key="props.fieldLabel"
      :fetch-suggestions="handleSearch"
      :placeholder="props.settings.placeholder || 'Buscar...'"
      :clearable="props.settings.clearable !== false"
      :disabled="props.settings.disabled"
      :size="props.settings.size || 'default'"
      :debounce="props.settings.debounce || 300"
      :trigger-on-focus="props.settings.triggerOnFocus || false"
      :loading="loading"
      style="width: 100%"
      @select="handleSelect"
      @clear="handleClear"
    >
      <template #default="{ item }">
        <slot
          name="option"
          :item="item"
        >
          <div>{{ item[props.fieldLabel] }}</div>
        </slot>
      </template>
      <template
        v-if="props.settings.hasAppend"
        #append
      >
        <slot name="append"></slot>
      </template>
      <template #prepend>
        <el-icon><IconSearch /></el-icon>
      </template>
    </el-autocomplete>
  </el-col>
</template>

<script setup>
import {
  ref,
  computed,
  watch,
} from 'vue';
import IconSearch from '~icons/mdi/search';

const props = defineProps({
  spanColumn: {
    type: Number,
    default: 24,
  },
  label: {
    type: String,
    default: '',
  },
  modelValue: {
    type: [String, Number, Object],
    default: '',
  },
  settings: {
    type: Object,
    default: () => ({
      placeholder: 'Buscar...',
      clearable: true,
      disabled: false,
      size: 'default',
      debounce: 300,
      triggerOnFocus: false,
      hideLabel: false,
      hasAppend: false,
    }),
  },
  fieldKey: {
    type: String,
    default: 'Codigo',
  },
  fieldLabel: {
    type: String,
    default: 'Descripcion',
  },
  minChars: {
    type: Number,
    default: 3,
  },
  pagination: {
    type: Object,
    default: () => ({
      enabled: false,
      page: 1,
      limit: 25,
    }),
  },
});

const emit = defineEmits(['update:modelValue', 'search', 'select', 'clear']);

const autocompleteRef = ref(null);
const loading = ref(false);
const currentPage = ref(props.pagination.page || 1);

const displayValue = computed({
  get() {
    if (typeof props.modelValue === 'object' && props.modelValue !== null) {
      return props.modelValue[props.fieldLabel] || '';
    }
    return props.modelValue || '';
  },
  set(value) {
    if (typeof props.modelValue === 'object') {
      emit('update:modelValue', { ...props.modelValue, [props.fieldLabel]: value });
    } else {
      emit('update:modelValue', value);
    }
  },
});

const handleSearch = async (queryString, callback) => {
  if (!queryString || queryString.length < props.minChars) {
    callback([]);
    return;
  }

  loading.value = true;
  currentPage.value = 1;

  try {
    const searchParams = {
      query: queryString,
      ...(props.pagination.enabled && {
        page: currentPage.value,
        limit: props.pagination.limit,
      }),
    };

    const results = await new Promise((resolve) => {
      emit('search', searchParams, resolve);
    });

    callback(results || []);
  } catch (error) {
    console.error('Error en búsqueda:', error);
    callback([]);
  } finally {
    loading.value = false;
  }
};

const handleSelect = (item) => {
  if (item) {
    emit('update:modelValue', item);
    emit('select', item);
  }
};

const handleClear = () => {
  emit('update:modelValue', typeof props.modelValue === 'object' ? {} : '');
  emit('clear');
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (!newValue || (typeof newValue === 'object' && Object.keys(newValue).length === 0)) {
      if (autocompleteRef.value) {
        displayValue.value = '';
      }
    }
  },
  { deep: true },
);

defineExpose({
  focus: () => {
    if (autocompleteRef.value) {
      autocompleteRef.value.focus();
    }
  },
});
</script>

<style scoped>
.label {
  font-weight: 700;
  color: #111;
}
</style>
